<script setup lang="ts">
    import { Button } from '@/components/ui/button';
    import {
        Dialog,
        DialogContent,
        DialogHeader,
        DialogTitle,
        DialogClose,
        DialogFooter,
        DialogTrigger,
        DialogBody,
    } from '@/components/ui/dialog';
    import { ref } from 'vue';
    import { useI18n } from 'vue-i18n';
    import FileUploadZone from '../FileUploadZone.vue';
    import CreateButton from '@/components/CreateButton.vue';
    import { Alert } from '@/components/ui/alert';
    import { toast } from 'vue-sonner';
    import QueryKey from '@/enums/QueryKey';
    import { useQueryClient } from '@tanstack/vue-query';
    import { usePage } from '@inertiajs/vue3';
    import type { SharedData } from '@/types';
    import { computed } from 'vue';
    
    const queryClient = useQueryClient();
    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const open = ref(false);
    const uploadedFile = ref([]);

    const { t } = useI18n();

    const handleClose = () => {
        uploadedFile.value = [];
        open.value = false;
    };

    const handleSuccess = () => {
        toast.success(t('forms.general.success'));
        queryClient.invalidateQueries({ queryKey: [QueryKey.POWER_BI, selectedCompanyId.value] });
    };
</script>

<template>
    <Dialog v-model:open="open">
        <DialogTrigger as-child>
            <CreateButton button-text="power_bi.upload_power_bi_report" />
        </DialogTrigger>
        <DialogContent>
            <DialogHeader>
                <DialogTitle>
                    {{ t('power_bi.upload_power_bi_report') }}
                </DialogTitle>
            </DialogHeader>

            <DialogBody class="space-y-5">
                <p class="text-muted-foreground text-xs">
                    {{ t('bi_and_companies.csv_headers_needed') }}
                    <span class="font-bold">
                        reporting account, reporting account nr, accounts in mapping, measurement
                        calculation
                    </span>
                </p>
                <Alert variant="warning">
                    {{ t('bi_and_companies.csv_import_warning') }}
                </Alert>
                <FileUploadZone
                    v-model="uploadedFile"
                    :accepts-files="['.csv']"
                    instant-upload
                    :route="route('v2.powerbi.setup.upload')"
                    :max-uploads="1"
                    @success="handleSuccess"
                />
            </DialogBody>

            <DialogFooter class="gap-2 border-t sm:justify-center">
                <DialogClose as-child>
                    <Button
                        variant="outline"
                        size="sm"
                        @click="handleClose"
                    >
                        {{ t('forms.close') }}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
